/**
 * Collection admin CRUD + public-read contracts.
 *
 * Admin: create / update (title, description, audiences, assigned events).
 * Public: GET by collection publicId (unguessable slug) — see
 * TicketedEventCollectionPublicSchema in the entities barrel.
 */

import { z } from 'zod'
import { TicketedEventAudienceSchema } from '../../schemas/entities/ticketed-event-audience'

// Audiences accepted on write: a de-duplicated set (metadata only, no
// enforcement). Empty set is allowed (an untagged collection).
const AudienceSetInput = z.array(TicketedEventAudienceSchema).transform(values => Array.from(new Set(values)))

export const CreateTicketedEventCollectionRequestSchema = z.object({
  title: z.string().min(1).max(500),
  description: z.string().max(20000).nullable().optional(),
  // Cloudinary secure URL of the collection's single wide image (optional).
  imageUrl: z.string().url().max(1000).nullable().optional(),
  // Reusable organizer publicId to brand the collection (optional; validated
  // server-side against the acting school — KTD2). null clears, undefined leaves.
  organizerId: z.string().nullable().optional(),
  audiences: AudienceSetInput.optional(),
  // Event public ids to assign to the collection (searchable multi-select on
  // the admin side). Optional — a collection can be created empty (F2).
  eventPublicIds: z.array(z.string()).optional()
})
export type CreateTicketedEventCollectionRequest = z.infer<typeof CreateTicketedEventCollectionRequestSchema>

export const UpdateTicketedEventCollectionRequestSchema = z.object({
  title: z.string().min(1).max(500).optional(),
  description: z.string().max(20000).nullable().optional(),
  // Cloudinary secure URL of the collection's single wide image (optional).
  imageUrl: z.string().url().max(1000).nullable().optional(),
  // Reusable organizer publicId to brand the collection (optional; validated
  // server-side against the acting school — KTD2). null clears, undefined leaves.
  organizerId: z.string().nullable().optional(),
  audiences: AudienceSetInput.optional(),
  // When present, replaces the collection's event membership with exactly this
  // set (assign + unassign in one call).
  eventPublicIds: z.array(z.string()).optional()
})
export type UpdateTicketedEventCollectionRequest = z.infer<typeof UpdateTicketedEventCollectionRequestSchema>

/**
 * DELETE response (R11–R13). `releasedEventCount` is measured BEFORE the delete
 * and is the number of the collection's events that actually became publicly
 * visible — published and not yet ended, NOT the raw event count. It is what
 * the confirmation promised, echoed back so the UI can confirm what happened.
 */
export const DeleteTicketedEventCollectionResponseSchema = z.object({
  id: z.string(),
  releasedEventCount: z.number().int()
})
export type DeleteTicketedEventCollectionResponse = z.infer<typeof DeleteTicketedEventCollectionResponseSchema>
